import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';

import {
  useCmsContent,
  useSuspendedQuery,
  useZepApiClient,
  UseZepApiQueryOptions,
} from '@zep/apis';
import { resolveContentId } from '@zep/next';

import { CmsEntries, ManageQueryKeys } from '../consts';
import { ApiCommonResponse, StoreItem, StoreItemSale } from '../types';
import { Donation, DonationsResponse } from '../types/DonationTypes';
import {
  ApiResponsePagerType,
  LandingSectionType,
  OfficeSectionType,
  ReferenceData,
} from '../types/GlobalTypes';

import { ZepApp } from './accountsService';

/**
 * 현재 내가 제작한 앱의 리스트를 가져오는 API(에셋 스토어 앱, 게임 업로드시 사용)
 * @returns 제작한 앱 목록
 */
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useMyApps = (
  options?: UseZepApiQueryOptions<{
    status: number;
    apps: ZepApp[];
  }>,
) => {
  const client = useZepApiClient();
  return useQuery(
    [ManageQueryKeys.MY_APP_LIST_GET],
    async () => {
      const { data } = await client.get('/v2/me/apps');

      return data;
    },

    // @ts-ignore
    options,
  );
};

/**
 * 후원 내역 가져오는 API
 * @param donationType 후원 방식 - ZEM / Token
 * @param page 페이지 번호
 * @param limit 가져올 개수
 * @param options
 * @returns 후원 내역
 */
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useDonations = (
  donationType: string,
  page = 1,
  limit = 10,
  options?: UseZepApiQueryOptions<{
    donations: Donation[];
    pager: ApiResponsePagerType;
    remainingAmount: number;
  }>,
) => {
  const client = useZepApiClient();
  const includeCharges = donationType === '0';
  const includeReceived = donationType === '0';

  return useQuery(
    [
      ManageQueryKeys.MY_DONATIONS,
      'sent',
      donationType,
      page,
      limit,
      includeCharges,
      includeReceived,
    ],
    async () => {
      const { data } = await client.get<DonationsResponse>(
        '/v2/me/donations/sent',
        {
          params: {
            type: donationType,
            ...(includeCharges ? { includeCharges: true } : {}),
            ...(includeReceived ? { includeReceived: true } : {}),
            page,
            limit,
          },
        },
      );
      const { results, pager, remainingAmount } = data;

      return {
        donations: results,
        pager,
        remainingAmount: remainingAmount ?? 0,
      };
    },

    // @ts-ignore
    { ...options, keepPreviousData: false },
  );
};

// 비필수 마케팅 CMS 콘텐츠 공통 옵션.
// 전역 QueryClient 가 useErrorBoundary:true 라서 곁다리 콘텐츠(배너/랜딩/explore)
// 하나만 실패해도 GlobalErrorBoundary 로 페이지 전체가 죽는다(WEB-APP-1QJ/1S5).
// 이 데이터들은 소비자가 모두 data?. 로 방어돼 있어 실패 시 섹션만 비우면 되므로
// 에러를 페이지로 던지지 않는다.
//
// zep.us/cms S3 의 간헐적 hang(WEB-APP-1QJ) 대응:
// - CMS 인스턴스 timeout 을 15s→8s 로 단축(libs/service/apis/.../cms/client.ts)해 재시도를 싸게 만들고
//   (정상 응답 p99.9=3.67s 라 정상 트래픽은 거의 안 자름)
// - 2회까지 고정 1s backoff 로 재시도해 일시적 실패에서 회복(노출률↑).
//   exponential backoff 는 timeout 과 겹쳐 헛도는 시간을 키우므로 고정 지연을 쓴다.
// - 한 번 받아온 콘텐츠는 5분간 fresh 로 유지해 재방문/포커스마다 hang 에 다시 노출되지 않게 한다.
const NON_CRITICAL_CMS_OPTIONS = {
  useErrorBoundary: false,
  retry: 2,
  retryDelay: 1000,
  staleTime: 5 * 60 * 1000,
} as const;

export function useBanners<
  T extends readonly unknown[] | Record<string, unknown>,
>(options?: UseZepApiQueryOptions<T>) {
  const { locale = 'en' } = useRouter();
  return useCmsContent<T>(resolveContentId(CmsEntries.BANNERS, locale), {
    ...NON_CRITICAL_CMS_OPTIONS,
    ...options,
  });
}

export function useExplore(options?: UseZepApiQueryOptions<ReferenceData>) {
  const { locale = 'en' } = useRouter();
  return useCmsContent<ReferenceData>(
    resolveContentId(CmsEntries.EXPLORE, locale),
    { ...NON_CRITICAL_CMS_OPTIONS, ...options },
  );
}

// 둘러보기 에듀 목록 — 위글루에서 코드로 내장(마지막 수정 2025-12-19, 사실상 동결된 콘텐츠).
// dynamic import 라 필요한 로케일 청크만 lazy 로드되고(ko 79KB), 네트워크 실패로 섹션이
// 빌 일이 없다. 갱신은 consts/explore-edu/*.json 수정 → 배포.
// 위글루 시절 id(인도네시아어) 로케일은 구형 포맷(배열)이라 이미 깨져 있었으므로 en 폴백으로 정리.
const EXPLORE_EDU_LOADERS: Record<string, () => Promise<{ default: unknown }>> =
  {
    ko: () => import('../consts/explore-edu/ko.json'),
    en: () => import('../consts/explore-edu/en.json'),
    ja: () => import('../consts/explore-edu/ja.json'),
  };

export function useExploreEdu(options?: UseZepApiQueryOptions<ReferenceData>) {
  const { locale = 'en' } = useRouter();
  const load = EXPLORE_EDU_LOADERS[locale] ?? EXPLORE_EDU_LOADERS.en;
  return useQuery(
    ['explore-edu', locale],
    async () => (await load()).default as ReferenceData,
    // @ts-ignore — 기존 UseZepApiQueryOptions 시그니처 유지(enabled 등 통과)
    options,
  );
}

export function useLanding(
  options?: UseZepApiQueryOptions<LandingSectionType>,
) {
  const { locale = 'en' } = useRouter();
  // staleTime 5분을 유지한다. useCmsContent 기본값은 10분인데, 랜딩은 CMS 에서 고친
  // 문구가 빨리 반영되는 게 이관의 목적이라 기존 정책을 그대로 넘긴다.
  return useCmsContent<LandingSectionType>(
    resolveContentId(CmsEntries.LANDING_PAGE, locale),
    { ...NON_CRITICAL_CMS_OPTIONS, ...options },
  );
}

export function useOfficeLanding(
  options?: UseZepApiQueryOptions<OfficeSectionType>,
) {
  const { locale = 'en' } = useRouter();
  return useCmsContent<OfficeSectionType>(
    resolveContentId(CmsEntries.OFFICE_PAGE, locale),
    { ...NON_CRITICAL_CMS_OPTIONS, ...options },
  );
}

export function useOnlineClassLanding(
  options?: UseZepApiQueryOptions<LandingSectionType>,
) {
  const { locale = 'en' } = useRouter();
  return useCmsContent<LandingSectionType>(
    resolveContentId(CmsEntries.ONLINE_CLASS_PAGE, locale),
    { ...NON_CRITICAL_CMS_OPTIONS, ...options },
  );
}

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useMyAssets = (page = 1, limit = 5) => {
  const client = useZepApiClient();
  const { locale } = useRouter();

  return useSuspendedQuery(
    [ManageQueryKeys.MY_ASSETS, page, limit],
    async () => {
      const { data } = await client.get<
        ApiCommonResponse & {
          data: {
            items: StoreItem[];
            itemCount: number;
            pager: ApiResponsePagerType;
          };
        }
      >('/v2/store/me/items', {
        params: {
          page,
          limit,
          locale,
        },
      });

      return data.data;
    },
  );
};

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useSales = (page = 1, limit = 20) => {
  const client = useZepApiClient();
  const { locale } = useRouter();

  return useSuspendedQuery(
    [ManageQueryKeys.MY_SALES, page, limit],
    async () => {
      const { data } = await client.get<
        ApiCommonResponse & {
          data: {
            purchases: StoreItemSale[];
            purchaseCount: number;
            totalRevenue: number;
            pager: ApiResponsePagerType;
          };
        }
      >('/v2/store/me/sales', {
        params: {
          page,
          limit,
          locale,
        },
      });

      const { purchaseCount, pager, totalRevenue = 0, purchases } = data.data;

      return {
        sales: purchases,
        totalRevenue,
        purchaseCount,
        pager,
      };
    },
  );
};
